import React from 'react';
import { LuCopy, LuEllipsis, LuLayers, LuPencil, LuTrash2 } from 'react-icons/lu';

import { AllocationType, AllocationWithUsage, Strategy } from '@ndvr/portfolio-api';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatPercent } from 'utils';

import { LibrarySelectionCheckbox } from '../LibrarySelectionCheckbox';
import { StackedCard } from '../StackedCard';
import { getStrategyTypeBadge } from '../strategies/strategy-utils';
import { TypeBadge } from '../TypeBadge';
import { isBlackrockAllocation } from './allocation-utils';

interface ModelCardProps {
    allocation: AllocationWithUsage;
    canEdit: boolean;
    selected: boolean;
    deleteBlockedReason?: string;
    onToggleSelect: () => void;
    onEdit: () => void;
    onClone: () => void;
    onDelete: () => void;
    onOpenStrategy: (strategy: Strategy) => void;
}

const STRATEGY_PREVIEW_COUNT = 3;

export const ModelCard: React.FC<ModelCardProps> = ({
    allocation,
    canEdit,
    selected,
    deleteBlockedReason,
    onToggleSelect,
    onEdit,
    onClone,
    onDelete,
    onOpenStrategy,
}) => {
    const isBlackrock = isBlackrockAllocation(allocation);
    const isClaro = allocation.allocationType === AllocationType.SHARED && !isBlackrock;
    const isOwned = allocation.allocationType === AllocationType.PERSONAL;
    const hasReferencingAccounts = (allocation.referencingAccountCount ?? 0) > 0;
    const sortedStrategyAllocations = [...allocation.strategyAllocations].sort(
        (a, b) => Number(b.weight) - Number(a.weight),
    );
    const visibleStrategyAllocations = sortedStrategyAllocations.slice(0, STRATEGY_PREVIEW_COUNT);
    const hiddenStrategyCount = sortedStrategyAllocations.length - visibleStrategyAllocations.length;

    return (
        <StackedCard>
            <div className="mb-3 flex items-center gap-2.5">
                <LibrarySelectionCheckbox
                    label={`Select model ${allocation.allocationName} for deletion`}
                    checked={selected}
                    onToggle={onToggleSelect}
                    disabledReason={deleteBlockedReason}
                />
                <TypeBadge label="Model" tone="model" icon={<LuLayers className="h-4 w-4" />} />
                {canEdit && (
                    <DropdownMenu>
                        <DropdownMenuTrigger className="text-cocoa-500 hover:bg-cocoa-50 ml-auto flex h-7 w-7 items-center justify-center rounded-md outline-none">
                            <LuEllipsis className="h-5 w-5" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="tracking-[-0.02em]">
                            <DropdownMenuItem onClick={onEdit} className="flex items-center gap-2">
                                <LuPencil className="h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={onClone} className="flex items-center gap-2">
                                <LuCopy className="h-4 w-4" /> Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={onDelete}
                                disabled={hasReferencingAccounts}
                                className="flex items-center gap-2 text-red-600 focus:text-red-600"
                            >
                                <LuTrash2 className="h-4 w-4" /> Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>

            <div className={canEdit ? 'cursor-pointer' : ''} onClick={canEdit ? onEdit : undefined}>
                <h3 className="text-cocoa-1000 truncate text-lg leading-[1.1] font-medium tracking-[-0.02em]">
                    {allocation.allocationName}
                </h3>
            </div>

            <div className="text-cocoa-500 mt-3 mb-2 text-[11px] font-semibold tracking-[0.06em] uppercase tabular-nums">
                {sortedStrategyAllocations.length} {sortedStrategyAllocations.length === 1 ? 'strategy' : 'strategies'}
            </div>

            <div className="flex-1">
                <div className="border-cocoa-200 bg-cocoa-0 ml-3.5 space-y-0.5 rounded-lg border p-1.5">
                    {visibleStrategyAllocations.map((strategyAllocation) => {
                        const badge = getStrategyTypeBadge(strategyAllocation);
                        return (
                            <button
                                type="button"
                                key={strategyAllocation.strategyGUID}
                                onClick={() => onOpenStrategy(strategyAllocation)}
                                className="border-cocoa-200/50 hover:bg-cocoa-0 flex w-full items-center gap-2 rounded border bg-white px-2.5 py-1.5 text-left"
                            >
                                {badge && <TypeBadge label={badge.label} tone={badge.tone} small />}
                                <span className="text-cocoa-1000 truncate text-[13px]">
                                    {strategyAllocation.strategyName}
                                </span>
                                <span className="text-cocoa-1000 ml-auto shrink-0 text-[13px] font-medium tabular-nums">
                                    {formatPercent(parseFloat(strategyAllocation.weight) * 100)}
                                </span>
                            </button>
                        );
                    })}
                    {hiddenStrategyCount > 0 && canEdit && (
                        <button
                            type="button"
                            onClick={onEdit}
                            className="text-cocoa-500 hover:text-cocoa-700 block w-full px-2.5 py-1 text-left text-xs"
                        >
                            +{hiddenStrategyCount} more
                        </button>
                    )}
                    {hiddenStrategyCount > 0 && !canEdit && (
                        <span className="text-cocoa-500 block w-full px-2.5 py-1 text-left text-xs">
                            +{hiddenStrategyCount} more
                        </span>
                    )}
                </div>
            </div>

            {(isClaro || isBlackrock || (isOwned && hasReferencingAccounts)) && (
                <div className="border-cocoa-200/50 mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
                    {isClaro && <TypeBadge label="Claro" tone="claro" small />}
                    {isBlackrock && <TypeBadge label="BlackRock" tone="blackrock" small />}
                    {isOwned && hasReferencingAccounts && (
                        <span className="text-cocoa-1000 text-[13px] tabular-nums">
                            Used in {allocation.referencingAccountCount}{' '}
                            {allocation.referencingAccountCount === 1 ? 'account' : 'accounts'}
                        </span>
                    )}
                </div>
            )}
        </StackedCard>
    );
};
